/**
 * Year in review - w2_18, rebuilt as a wrap in dark_05 (Diary's wrap style).
 *
 * YearReview: up to eight pages you swipe through - the big numbers, net
 *   worth, where it went, sinking funds, no-spend days, highlights, a share
 *   card you can save as a picture, and "That's a wrap!". A page only shows
 *   when there's something for it.
 * YearReviewPopup: on Home from December 15 (this year) and January 8-31
 *   (last year). "Maybe later" brings it back three days later. It also opens
 *   from Settings > Developer > Preview year in review.
 * YearReviewRow: the row at the top of Budget > stats > Reviews. With one
 *   year it opens that year; with two or more it opens a sheet of years.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import confetti from "canvas-confetti";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { formatCurrencyWhole } from "../../utils/formatters";
import { MONTHS, buildYearReview, hasYearReview, reviewYearFor } from "../../utils/yearReview";
import { monthTotals, feelingSummary } from "../../utils/monthEndReview";

const K = {
  page: '#F1EBFC', ink: '#26252E', soft: '#8B879A', line: '#E3DEF0', btn: '#1F1E24',
  lil: '#ECE6FA', lilT: '#7A5DD3', mint: '#DFF2E7', mintT: '#2F8756',
  blue: '#DFE9F9', blueT: '#3C6DB5', pink: '#FAE4ED', pinkT: '#C04B7C',
};
// Soft colour + its text colour, in the order pages use them.
const TINTS = [[K.mint, K.mintT], [K.blue, K.blueT], [K.lil, K.lilT], [K.pink, K.pinkT]];
const RANK = ['#E58AAE', '#7FA6DE', '#A68EEA', '#5DB88A', '#E9A27E'];
const money = (v) => formatCurrencyWhole(Math.round(v));
const signed = (v) => (v >= 0 ? '+' : '−') + formatCurrencyWhole(Math.abs(Math.round(v)));
const plural = (n, one, many) => `${n.toLocaleString()} ${n === 1 ? one : many}`;
// Native plugins (installed by dark_05). On the web we just download the picture.
const Filesystem = registerPlugin('Filesystem');
const Share = registerPlugin('Share');
const CONFETTI = ['#B9A6F0', '#9ED9B6', '#F2AFC8', '#A9C6F0'];
const mkey = (y, m) => `${y}-${String(m + 1).padStart(2, '0')}`;

/* ------------------------------------------------------------------ the pieces */
const Eyebrow = ({ e, children }) => (
  <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, fontWeight: 600, letterSpacing: '1.6px', textTransform: 'uppercase', color: K.lilT }}>{e} {children}</span>
);
const Title = ({ small, children }) => (
  <span style={{ display: 'block', fontSize: small ? 25 : 32, fontWeight: 600, letterSpacing: small ? '-0.8px' : '-1.2px', lineHeight: 1.08, color: K.ink, margin: small ? '8px 0 14px' : '8px 0 6px' }}>{children}</span>
);
const Tile = ({ i, e, v, l }) => {
  const [bg, ink] = TINTS[i % TINTS.length];
  return (
    <div style={{ background: '#FFFFFF', borderRadius: 20, padding: '13px 14px', minWidth: 0 }}>
      <span className="flex items-center justify-center" style={{ width: 30, height: 30, borderRadius: 10, background: bg, fontSize: 15 }}>{e}</span>
      <span className="truncate" style={{ display: 'block', fontSize: 26, fontWeight: 600, letterSpacing: '-0.8px', color: ink, marginTop: 8 }}>{v}</span>
      <span style={{ display: 'block', fontSize: 12.5, color: K.soft, fontWeight: 500, lineHeight: 1.3, marginTop: 1 }}>{l}</span>
    </div>
  );
};
const Row = ({ i, e, b, s, right, tag }) => {
  const [bg, ink] = TINTS[i % TINTS.length];
  return (
    <div className="flex items-center" style={{ gap: 12, background: '#FFFFFF', borderRadius: 20, padding: 12, marginBottom: 9 }}>
      <span className="flex items-center justify-center flex-shrink-0" style={{ width: 40, height: 40, borderRadius: 13, background: bg, fontSize: 19 }}>{e}</span>
      <span style={{ minWidth: 0, flex: 1 }}>
        <span style={{ display: 'block', fontSize: 11.5, color: K.soft, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>{b}</span>
        <span style={{ display: 'block', fontSize: 15.5, fontWeight: 600, color: K.ink, marginTop: 2 }}>
          {s}
          {tag && <span style={{ display: 'inline-block', fontSize: 11, fontWeight: 600, borderRadius: 999, padding: '3px 8px', marginLeft: 6, verticalAlign: 2, background: K.mint, color: K.mintT }}>{tag}</span>}
        </span>
      </span>
      {right && <span style={{ marginLeft: 'auto', fontSize: 15, fontWeight: 600, color: ink, flexShrink: 0 }}>{right}</span>}
    </div>
  );
};

/* ------------------------------------------------------------------ extra sums */
function extras(userData, r) {
  const tx = (userData.transactions || []).filter((t) => String(t.month_key || '').startsWith(r.year + '-'));
  const from = r.firstYear ? r.startMonth : 0;
  const months = [];
  for (let m = from; m <= r.endMonth; m += 1) {
    const t = monthTotals(tx, mkey(r.year, m));
    months.push({ m, saved: t.saved, buys: t.expenses.length, any: t.tx.length > 0 });
  }
  const tracked = months.filter((x) => x.any).length;
  const bestSave = months.filter((x) => x.saved > 0).sort((a, b) => b.saved - a.saved)[0] || null;
  const busiest = months.filter((x) => x.buys > 0).sort((a, b) => b.buys - a.buys)[0] || null;
  const expenses = tx.filter((t) => !t.is_income && !t.is_savings && !t.is_transfer);
  const fs = feelingSummary(expenses);
  const topFeel = fs.tagged >= 5 ? fs.rows.slice().sort((a, b) => b.count - a.count)[0] : null;
  // no-spend days, every day of the year, and the longest run
  const ns = userData.no_spend_days || {};
  const nsSet = {};
  let run = 0; let best = 0;
  for (let m = 0; m < 12; m += 1) {
    const set = new Set(((ns[mkey(r.year, m)] || [])).map(Number));
    nsSet[m] = set;
    const days = new Date(r.year, m + 1, 0).getDate();
    for (let d = 1; d <= days; d += 1) {
      if (set.has(d)) { run += 1; best = Math.max(best, run); } else run = 0;
    }
  }
  return { tracked, bestSave, busiest, topFeel, nsSet, nsRun: best };
}

/* ------------------------------------------------------------------ save as a picture */
const loadFonts = async () => { try { await document.fonts.ready; } catch (e) { /* fine */ } };
function rr(ctx, x, y, w, h, r) { ctx.beginPath(); if (ctx.roundRect) ctx.roundRect(x, y, w, h, r); else ctx.rect(x, y, w, h); }

async function makePicture({ name, year, tiles }) {
  await loadFonts();
  const W = 1080; const H = 1350;
  const c = document.createElement('canvas');
  c.width = W; c.height = H;
  const x = c.getContext('2d');
  const F = (sz) => `600 ${sz}px "DM Sans", -apple-system, sans-serif`;
  x.fillStyle = K.page; x.fillRect(0, 0, W, H);
  x.fillStyle = '#FFFFFF'; rr(x, 60, 120, 960, 1110, 64); x.fill();
  x.font = F(30); x.fillStyle = K.lilT; x.fillText(`MY ${year} WITH WORTHLY`, 120, 215);
  x.font = F(76); x.fillStyle = K.ink; x.fillText(`${name ? name + "'s " : ''}${year} \u{1F49C}`, 120, 310);
  tiles.slice(0, 4).forEach((t, i) => {
    const [bg, ink] = TINTS[t.i % TINTS.length];
    const tx = 120 + (i % 2) * 430; const ty = 380 + Math.floor(i / 2) * 340;
    x.fillStyle = bg; rr(x, tx, ty, 410, 310, 44); x.fill();
    x.fillStyle = 'rgba(255,255,255,0.75)'; rr(x, tx + 34, ty + 34, 72, 72, 22); x.fill();
    x.font = F(38); x.fillStyle = '#000'; x.textAlign = 'center'; x.fillText(t.e, tx + 70, ty + 84); x.textAlign = 'left';
    x.font = F(t.v.length > 8 ? 56 : 68); x.fillStyle = ink; x.fillText(t.v, tx + 34, ty + 200);
    x.font = F(30); x.fillStyle = '#6C6879'; x.fillText(t.l, tx + 34, ty + 254);
  });
  x.font = F(28); x.fillStyle = K.soft; x.fillText('worthlyhq.com', 120, 1160);
  x.fillStyle = K.lilT; x.textAlign = 'right'; x.fillText('Worthly', 960, 1160); x.textAlign = 'left';
  const dataUrl = c.toDataURL('image/png');
  const b64 = dataUrl.split(',')[1];
  if (!Capacitor.isNativePlatform()) {
    const a = document.createElement('a'); a.href = dataUrl; a.download = `worthly-${year}.png`; a.click();
    return;
  }
  const path = `worthly-${year}-wrapped.png`;
  await Filesystem.writeFile({ path, data: b64, directory: 'CACHE' });
  const { uri } = await Filesystem.getUri({ path, directory: 'CACHE' });
  // The share sheet has "Save Image". The first time, iOS asks to add it to Photos.
  await Share.share({ title: `My ${year} with Worthly`, files: [uri] });
}

/* ------------------------------------------------------------------ the pages */
function buildPages(r, x) {
  const pages = [];
  const nw = r.netWorth;
  const hasNw = nw.series.length >= 2;
  const first = (r.name || '').trim().split(/\s+/)[0];
  const since = r.firstYear ? MONTHS[r.startMonth] : null;
  const pct = hasNw && nw.start > 0 ? Math.round((nw.change / nw.start) * 100) : null;

  // the big numbers (also used on the share card)
  const big = [];
  if (hasNw) big.push({ i: 0, e: '\u{1F4C8}', v: signed(nw.change), l: 'net worth growth' });
  if (r.saved > 0) big.push({ i: 1, e: '\u{1F4B0}', v: money(r.saved), l: 'saved and invested' });
  if (r.funds.added > 0) big.push({ i: 2, e: '\u{1F437}', v: money(r.funds.added), l: 'into your funds' });
  if (r.noSpend.days > 0) big.push({ i: 3, e: '\u{1F3AF}', v: r.noSpend.days.toLocaleString(), l: 'no-spend days' });
  if (r.purchases > 0) big.push({ i: 1, e: '\u{1F9FE}', v: r.purchases.toLocaleString(), l: 'purchases logged' });
  if (x.tracked > 0) big.push({ i: 3, e: '\u{1F4C5}', v: String(x.tracked), l: x.tracked === 1 ? 'month tracked' : 'months tracked' });

  // 1. Your year, wrapped!
  pages.push({
    key: 'open', badge: true,
    body: (
      <>
        <Eyebrow e="">{r.year} with Worthly</Eyebrow>
        <span style={{ display: 'block', fontSize: 32, fontWeight: 600, letterSpacing: '-1.2px', lineHeight: 1.08, color: K.ink, margin: '8px 0 6px' }}>Your {r.year},<br />wrapped!</span>
        <span style={{ display: 'block', fontSize: 15.5, color: K.soft, fontWeight: 500, marginBottom: 18 }}>{first ? `What a year, ${first}!` : 'What a year!'}</span>
        <div className="grid grid-cols-2" style={{ gap: 10 }}>
          {big.slice(0, 6).map((t) => <Tile key={t.l} {...t} />)}
        </div>
      </>
    ),
  });

  // 2. Net worth
  if (hasNw) {
    const up = nw.change > 0.5;
    pages.push({
      key: 'nw',
      body: (
        <>
          <Eyebrow e={'\u{1F4C8}'}>Net worth</Eyebrow>
          <Title small>{up ? `You grew your net worth by ${money(nw.change)}!` : `Your net worth ended the year at ${money(nw.end)}`}</Title>
          <Row i={0} e={'\u{1F331}'} b={since ? `Started in ${since}` : 'Started the year'} s={money(nw.start)} />
          <Row i={1} e={'\u{1F3C1}'} b="Ended the year" s={money(nw.end)} />
          {nw.best && nw.best.jump > 0 && <Row i={2} e={'\u{1F680}'} b="Biggest jump" s={MONTHS[nw.best.m]} right={signed(nw.best.jump)} />}
          {up && pct !== null && <Row i={3} e={'\u{1F4AF}'} b="Up by" s={`${pct}% ${since ? `since ${since}` : 'this year'}`} />}
          {up && nw.biggestYet && <Row i={0} e={'\u{1F3C6}'} b="A first" s="Your biggest year yet!" />}
        </>
      ),
    });
  }

  // 3. Where it went
  if (r.top.length >= 3) {
    const weeks = Math.max(1, Math.round(((r.endMonth - (r.firstYear ? r.startMonth : 0) + 1) * 52) / 12));
    pages.push({
      key: 'spend',
      body: (
        <>
          <Eyebrow e={'\u{1F3C6}'}>Where it went</Eyebrow>
          <Title small>Your top spending</Title>
          <div style={{ background: '#FFFFFF', borderRadius: 20, padding: '2px 14px' }}>
            {r.top.slice(0, 5).map((t, i) => (
              <div key={t.category} className="flex items-center" style={{ gap: 10, padding: '12px 0', borderTop: i ? `1px solid ${K.line}` : 0 }}>
                <span className="flex items-center justify-center flex-shrink-0" style={{ width: 24, height: 24, borderRadius: 999, background: RANK[i], color: '#FFFFFF', fontSize: 12, fontWeight: 600 }}>{i + 1}</span>
                <span className="truncate" style={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 600, color: K.ink }}>{t.emoji} {t.name}</span>
                <span style={{ fontSize: 13, color: K.soft, fontWeight: 600, flexShrink: 0 }}>{money(t.total)}</span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 12 }}>
            <Row i={1} e={'\u{1F9FE}'} b="Purchases logged" s={`${r.purchases.toLocaleString()}, about ${Math.max(1, Math.round(r.purchases / weeks))} a week`} />
          </div>
          <span style={{ display: 'block', fontSize: 12, color: K.soft, textAlign: 'center' }}>Rent is left out, so you see the things you chose.</span>
        </>
      ),
    });
  }

  // 4. Sinking funds
  if (r.funds.added > 0) {
    const filled = new Set(r.funds.filled.map((f) => f.id));
    pages.push({
      key: 'funds',
      body: (
        <>
          <Eyebrow e={'\u{1F437}'}>Sinking funds</Eyebrow>
          <Title small>You put {money(r.funds.added)} into your funds!</Title>
          {r.funds.top.slice(0, 5).map((f, i) => (
            <Row key={f.id} i={i} e={f.emoji} b={f.name} s={`+${money(f.added)}`} tag={filled.has(f.id) ? 'Filled up!' : null} />
          ))}
          {r.funds.used > 0 && <span style={{ display: 'block', fontSize: 12.5, color: K.soft, textAlign: 'center', marginTop: 4 }}>And you used {money(r.funds.used)} from them for the things you saved for!</span>}
        </>
      ),
    });
  }

  // 5. No-spend days
  if (r.noSpend.days >= 5) {
    pages.push({
      key: 'ns',
      body: (
        <>
          <Eyebrow e={'\u{1F3AF}'}>No-spend days</Eyebrow>
          <Title small>{r.noSpend.days} days without spending!</Title>
          <div style={{ background: '#FFFFFF', borderRadius: 20, padding: 12 }}>
            {MONTHS.map((name, m) => {
              const days = new Date(r.year, m + 1, 0).getDate();
              return (
                <div key={name} className="flex items-center" style={{ gap: 6, marginBottom: m === 11 ? 0 : 3 }}>
                  <span style={{ width: 10, fontSize: 9.5, fontWeight: 600, color: K.soft }}>{name[0]}</span>
                  <div className="grid" style={{ flex: 1, gridTemplateColumns: 'repeat(31, 1fr)', gap: 2 }}>
                    {Array.from({ length: days }, (_, d) => (
                      <i key={d} style={{ display: 'block', aspectRatio: '1', borderRadius: 2, background: x.nsSet[m] && x.nsSet[m].has(d + 1) ? '#7FCBA0' : K.lil }} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 14 }}>
            {x.nsRun > 1 && <Row i={2} e={'\u{1F525}'} b="Longest streak" s={`${x.nsRun} days in a row`} />}
            {r.noSpend.best && <Row i={3} e={'\u{1F4C5}'} b="Best month" s={`${MONTHS[r.noSpend.best.m]}, ${plural(r.noSpend.best.days, 'day', 'days')}`} />}
          </div>
        </>
      ),
    });
  }

  // 6. Highlights
  const hl = [];
  if (x.bestSave) hl.push(<Row key="save" i={0} e={'\u{1F4B0}'} b="Best saving month" s={MONTHS[x.bestSave.m]} right={money(x.bestSave.saved)} />);
  if (x.busiest) hl.push(<Row key="busy" i={1} e={'\u{1F5D3}️'} b="Busiest month" s={`${MONTHS[x.busiest.m]}, ${plural(x.busiest.buys, 'purchase', 'purchases')}`} />);
  if (x.topFeel) hl.push(<Row key="feel" i={2} e={x.topFeel.emoji} b="How it felt" s={`Mostly ${x.topFeel.label.toLowerCase()}`} />);
  if (r.funds.filled.length) hl.push(<Row key="filled" i={3} e={'\u{1F6DF}'} b="Funds filled" s={r.funds.filled.slice(0, 3).map((f) => f.name).join(', ')} />);
  if (r.noSpend.wins > 0) hl.push(<Row key="wins" i={0} e={'\u{1F3C6}'} b="Willpower wins" s={`${money(r.noSpend.winsAmount)} not spent`} />);
  if (hl.length >= 2) {
    pages.push({ key: 'hl', body: (<><Eyebrow e={'⭐'}>Highlights</Eyebrow><Title small>The best bits</Title>{hl}</>) });
  }

  // 7. Share it
  const shareTiles = big.slice(0, 4);
  pages.push({
    key: 'share', share: { name: first, year: r.year, tiles: shareTiles },
    body: (
      <>
        <Eyebrow e={'\u{1F48C}'}>Share it</Eyebrow>
        <Title small>Your {r.year} on one page</Title>
        <div style={{ background: '#FFFFFF', borderRadius: 26, padding: 18, boxShadow: '0 8px 24px rgba(80,60,140,0.08)' }}>
          <span style={{ display: 'block', fontSize: 20, fontWeight: 600, letterSpacing: '-0.5px', color: K.ink }}>{first ? `${first}'s` : 'My'} {r.year} {'\u{1F49C}'}</span>
          <span style={{ display: 'block', fontSize: 12.5, color: K.soft, marginTop: 2 }}>My year with Worthly</span>
          <div className="grid grid-cols-2" style={{ gap: 7, marginTop: 12 }}>
            {shareTiles.map((t) => {
              const [bg, ink] = TINTS[t.i % TINTS.length];
              return (
                <div key={t.l} style={{ background: bg, borderRadius: 18, padding: '11px 12px', minWidth: 0 }}>
                  <span className="flex items-center justify-center" style={{ width: 30, height: 30, borderRadius: 10, background: 'rgba(255,255,255,0.7)', fontSize: 15 }}>{t.e}</span>
                  <span className="truncate" style={{ display: 'block', fontSize: 21, fontWeight: 600, letterSpacing: '-0.6px', color: ink, marginTop: 6 }}>{t.v}</span>
                  <span style={{ display: 'block', fontSize: 11.5, color: K.soft, fontWeight: 500 }}>{t.l}</span>
                </div>
              );
            })}
          </div>
          <div className="flex justify-between" style={{ marginTop: 12, fontSize: 11.5, color: K.soft }}>
            <span>worthlyhq.com</span><span style={{ color: K.lilT, fontWeight: 600 }}>Worthly</span>
          </div>
        </div>
      </>
    ),
  });

  // 8. That's a wrap!
  pages.push({
    key: 'end', end: true,
    body: (
      <div className="flex flex-col items-center justify-center text-center" style={{ flex: 1 }}>
        <span style={{ fontSize: 72, lineHeight: 1 }}>{'\u{1F389}'}</span>
        <span style={{ display: 'block', fontSize: 32, fontWeight: 600, letterSpacing: '-1.2px', color: K.ink, marginTop: 16 }}>That's a wrap!</span>
        <span style={{ display: 'block', fontSize: 15.5, color: K.soft, fontWeight: 500, marginTop: 6 }}>Have a great {r.year + 1}{first ? `, ${first}` : ''}!</span>
      </div>
    ),
  });
  return pages;
}

/* ------------------------------------------------------------------ the swipe */
export default function YearReview({ userData, year, onClose }) {
  const r = useMemo(() => buildYearReview(userData || {}, year), [userData, year]);
  const x = useMemo(() => extras(userData || {}, r), [userData, r]);
  const pages = useMemo(() => buildPages(r, x), [r, x]);
  const [page, setPage] = useState(0);
  const [saving, setSaving] = useState(false);
  const ref = useRef(null);
  const canvas = useRef(null);

  useEffect(() => {
    if (!canvas.current) return undefined;
    const fire = confetti.create(canvas.current, { resize: true });
    const t = setTimeout(() => { try { fire({ particleCount: 90, spread: 70, origin: { y: 0.3 }, colors: CONFETTI }); } catch (e) { /* no confetti */ } }, 350);
    return () => { clearTimeout(t); try { fire.reset(); } catch (e) { /* already gone */ } };
  }, []);

  const go = (i) => {
    const el = ref.current;
    if (!el) return;
    el.scrollTo({ left: Math.max(0, Math.min(pages.length - 1, i)) * el.clientWidth, behavior: 'smooth' });
  };
  const onScroll = () => {
    const el = ref.current;
    if (!el || !el.clientWidth) return;
    const i = Math.round(el.scrollLeft / el.clientWidth);
    if (i !== page) setPage(i);
  };
  const save = async (share) => {
    if (saving) return;
    setSaving(true);
    try { await makePicture(share); } catch (e) { /* cancelled */ }
    setSaving(false);
  };

  return createPortal(
    <div className="fixed inset-0 flex flex-col" style={{ zIndex: 120, background: K.page, fontFamily: "'DM Sans', -apple-system, sans-serif" }} data-testid="year-review" data-yr-wrap="">
      <canvas ref={canvas} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 3 }} />
      <button type="button" aria-label="Close" onClick={onClose} className="flex items-center justify-center" style={{ position: 'absolute', top: 'calc(env(safe-area-inset-top, 0px) + 14px)', right: 18, zIndex: 2, width: 34, height: 34, borderRadius: 999, border: 0, background: '#FFFFFF', color: K.soft, fontSize: 14 }}>✕</button>
      <div ref={ref} onScroll={onScroll} className="flex" style={{ flex: 1, minHeight: 0, overflowX: 'auto', overflowY: 'hidden', scrollSnapType: 'x mandatory', scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
        {pages.map((p) => (
          <section key={p.key} className="flex flex-col" style={{ position: 'relative', flex: '0 0 100%', scrollSnapAlign: 'start', boxSizing: 'border-box', overflowY: 'auto', padding: 'calc(env(safe-area-inset-top, 0px) + 64px) 22px 12px' }}>
            {p.badge && (
              <span className="flex items-center justify-center" style={{ position: 'absolute', right: 22, top: 'calc(env(safe-area-inset-top, 0px) + 60px)', width: 54, height: 54, borderRadius: 999, background: '#FFFFFF', fontSize: 27, boxShadow: '0 6px 18px rgba(122,93,211,0.15)' }}>{'\u{1F389}'}</span>
            )}
            <div style={{ maxWidth: 520, width: '100%', margin: '0 auto', display: 'flex', flexDirection: 'column', flex: 1 }}>
              {p.body}
              {p.share && (
                <div style={{ marginTop: 'auto', paddingTop: 18 }}>
                  <button type="button" onClick={() => save(p.share)} disabled={saving} style={{ display: 'block', width: '100%', border: 0, borderRadius: 999, background: K.btn, color: '#FFFFFF', fontSize: 15.5, fontWeight: 600, padding: 15, fontFamily: 'inherit', opacity: saving ? 0.6 : 1 }}>
                    {saving ? 'Making your picture…' : '\u{1F4E4} Save as a picture'}
                  </button>
                  <span style={{ display: 'block', fontSize: 12, color: K.soft, textAlign: 'center', marginTop: 10, lineHeight: 1.45 }}>The first time, your phone asks if Worthly can add it to your Photos.</span>
                </div>
              )}
              {p.end && (
                <button type="button" onClick={onClose} style={{ display: 'block', width: '100%', border: 0, borderRadius: 999, background: K.btn, color: '#FFFFFF', fontSize: 15.5, fontWeight: 600, padding: 15, fontFamily: 'inherit', marginBottom: 'calc(env(safe-area-inset-bottom, 0px) + 40%)' }}>Done</button>
              )}
            </div>
          </section>
        ))}
      </div>
      <div className="flex justify-center" style={{ gap: 6, padding: '10px 0 calc(env(safe-area-inset-bottom, 0px) + 18px)' }}>
        {pages.map((p, i) => (
          <button key={p.key} type="button" aria-label={`Page ${i + 1}`} onClick={() => go(i)} style={{ width: i === page ? 20 : 7, height: 7, borderRadius: 9, border: 0, padding: 0, background: K.ink, opacity: i === page ? 1 : 0.16, transition: 'width .2s' }} />
        ))}
      </div>
    </div>,
    document.body
  );
}

/* ------------------------------------------------------------------ Home pop-up */
const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
export const PREVIEW_YEAR_REVIEW_EVENT = 'worthly:preview-year-review';

export function YearReviewPopup({ userData, saveUserData, suppressed = false }) {
  const [open, setOpen] = useState(null); // year being viewed
  const [hidden, setHidden] = useState(false);
  useEffect(() => {
    const onPreview = () => setOpen(new Date().getFullYear());
    window.addEventListener(PREVIEW_YEAR_REVIEW_EVENT, onPreview);
    return () => window.removeEventListener(PREVIEW_YEAR_REVIEW_EVENT, onPreview);
  }, []);
  const today = new Date();
  const year = reviewYearFor(today);
  const state = (userData && userData.year_review) || {};
  const seen = (state.seen || {})[year];
  const later = state.later_until && state.later_until > dayKey(today);
  const inJanuaryTooEarly = today.getMonth() === 0 && today.getDate() < 8; // the December month-end review comes first
  const show = !open && !hidden && !suppressed && year && !seen && !later && !inJanuaryTooEarly && hasYearReview(userData || {}, year, today);
  const mark = (patch) => saveUserData && saveUserData({ year_review: { ...state, ...patch } });

  if (open) return <YearReview userData={userData} year={open} onClose={() => setOpen(null)} />;
  if (!show) return null;
  const tiles = [['\u{1F4C8}', 'Net worth', K.mint, K.mintT], ['\u{1F6CD}️', 'Top spend', K.pink, K.pinkT], ['\u{1F437}', 'Sinking funds', K.blue, K.blueT], ['\u{1F3AF}', 'No-spend days', K.lil, K.lilT]];
  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 9999, padding: 20 }} data-testid="year-review-popup">
      <div className="absolute inset-0" style={{ background: 'rgba(30,25,50,.30)' }} />
      <div className="relative w-full" style={{ maxWidth: 360, background: '#FFFFFF', borderRadius: 32, padding: '24px 22px 16px', boxShadow: '0 30px 60px rgba(28,27,31,0.18)' }}>
        <div className="grid grid-cols-2" style={{ gap: 8 }}>
          {tiles.map(([e, l, bg, ink]) => (
            <div key={l} style={{ background: bg, borderRadius: 18, padding: '16px 8px', textAlign: 'center' }}>
              <div style={{ fontSize: 21 }}>{e}</div>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: ink, marginTop: 6 }}>{l}</div>
            </div>
          ))}
        </div>
        <div style={{ fontSize: 27, fontWeight: 600, letterSpacing: '-0.9px', lineHeight: 1.12, color: K.ink, marginTop: 18 }}>Your {year}, wrapped! {'\u{1F389}'}</div>
        <p style={{ fontSize: 15, lineHeight: 1.5, color: '#55525F', marginTop: 10 }}>See how your year went, from your net worth to your top spend and your sinking funds!</p>
        <button type="button" onClick={() => { mark({ seen: { ...(state.seen || {}), [year]: true } }); setOpen(year); }} style={{ display: 'block', width: '100%', marginTop: 22, border: 0, borderRadius: 999, background: K.btn, color: '#FFFFFF', fontSize: 16, fontWeight: 600, padding: 16, fontFamily: 'inherit' }}>Let's see it</button>
        <button type="button" onClick={() => { const d = new Date(); d.setDate(d.getDate() + 3); mark({ later_until: dayKey(d) }); setHidden(true); }} style={{ display: 'block', width: '100%', marginTop: 10, border: `1.5px solid ${K.btn}`, borderRadius: 999, background: 'transparent', color: K.btn, fontSize: 15, fontWeight: 600, padding: 14, fontFamily: 'inherit' }}>Maybe later</button>
      </div>
    </div>,
    document.body
  );
}

/* ------------------------------------------------------------------ Reviews row */
export function YearReviewRow({ userData, saveUserData }) {
  const [open, setOpen] = useState(null);
  const [sheet, setSheet] = useState(false);
  const today = new Date();
  const years = useMemo(() => {
    const out = [];
    const cur = today.getFullYear();
    const lastReady = today.getMonth() === 11 && today.getDate() >= 15 ? cur : cur - 1;
    for (let y = lastReady; y >= 2024; y -= 1) if (hasYearReview(userData || {}, y, today)) out.push(y);
    return out;
  }, [userData]); // eslint-disable-line react-hooks/exhaustive-deps
  const summaries = useMemo(() => (sheet ? years.map((y) => ({ y, r: buildYearReview(userData || {}, y, today) })) : []), [sheet, years]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!years.length) return null;
  const openYear = (y) => {
    setSheet(false);
    setOpen(y);
    const state = (userData && userData.year_review) || {};
    if (saveUserData && !(state.seen || {})[y]) saveUserData({ year_review: { ...state, seen: { ...(state.seen || {}), [y]: true } } });
  };
  const one = years.length === 1;
  return (
    <>
      <button type="button" onClick={() => (one ? openYear(years[0]) : setSheet(true))} className="flex items-center w-full text-left" style={{ gap: 14, background: K.lil, borderRadius: 24, padding: 16, border: 0, marginBottom: 12, fontFamily: 'inherit' }} data-testid="year-review-row">
        <span className="flex items-center justify-center flex-shrink-0" style={{ width: 46, height: 46, borderRadius: 16, background: '#FFFFFF', fontSize: 21 }}>{'\u{1F389}'}</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 16, fontWeight: 600, color: K.ink }}>{one ? `Your ${years[0]}, wrapped` : 'Your years, wrapped'}</span>
          <span style={{ display: 'block', fontSize: 13, color: K.lilT, marginTop: 1 }}>{one ? 'Your whole year, page by page' : years.join(' · ')}</span>
        </span>
        <span style={{ color: K.lilT, fontSize: 20 }}>{'›'}</span>
      </button>
      {sheet && createPortal(
        <div className="fixed inset-0" style={{ zIndex: 110 }}>
          <div className="absolute inset-0" style={{ background: 'rgba(30,25,50,.30)' }} onClick={() => setSheet(false)} />
          <div className="absolute left-0 right-0 bottom-0" style={{ background: '#FFFFFF', borderRadius: '30px 30px 0 0', padding: '12px 18px calc(env(safe-area-inset-bottom, 0px) + 26px)', maxWidth: 560, margin: '0 auto' }}>
            <div style={{ width: 38, height: 5, borderRadius: 3, background: '#E3DFEC', margin: '0 auto 14px' }} />
            <div style={{ fontSize: 19, fontWeight: 600, color: K.ink }}>Your years, wrapped {'\u{1F389}'}</div>
            <div style={{ fontSize: 13.5, color: K.soft, marginTop: 3 }}>Tap a year to swipe through it again</div>
            <div className="grid grid-cols-2" style={{ gap: 10, marginTop: 14 }}>
              {summaries.map(({ y, r }, i) => {
                const c = TINTS[i % TINTS.length];
                return (
                  <button key={y} type="button" onClick={() => openYear(y)} className="text-left" style={{ background: c[0], borderRadius: 22, padding: '16px 14px', border: 0, fontFamily: 'inherit' }}>
                    <span style={{ display: 'block', fontSize: 26, fontWeight: 600, letterSpacing: '-0.8px', color: c[1] }}>{y}</span>
                    {r.netWorth.series.length >= 2 && <span style={{ display: 'block', fontSize: 12.5, color: c[1], marginTop: 6 }}>Net worth {signed(r.netWorth.change)}</span>}
                    {r.top[0] && <span className="truncate" style={{ display: 'block', fontSize: 12.5, color: 'rgba(38,37,46,.6)', marginTop: 2 }}>{r.top[0].emoji} {r.top[0].name}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>,
        document.body
      )}
      {open && <YearReview userData={userData} year={open} onClose={() => setOpen(null)} />}
    </>
  );
}
